/**
 * Global support context — circular buffers for console errors and network
 * failures, hydrated automatically by setupSupportContext() at app boot.
 *
 * Consumed by SupportWidget when the user creates a ticket; gives doctor-agent
 * (PR-5) and triage-agent (PR-4) the breadcrumbs needed to reproduce a bug
 * without asking the user "open DevTools and paste console" — that pattern
 * is explicitly forbidden by CLAUDE.md §12.
 */
import { isExpectedAbort } from './abortError'

const ERROR_BUFFER_LIMIT = 20
const NETWORK_BUFFER_LIMIT = 20
const NETWORK_WINDOW_MS = 60_000

const _errors = []
const _networkFailures = []
let _installed = false

function _pushBounded(arr, entry, limit) {
    arr.push(entry)
    if (arr.length > limit) arr.splice(0, arr.length - limit)
}

const _EXTENSION_URL_RE = /(chrome|moz|safari|safari-web|edge|webkit)-extension:\/\//i
const _WALLET_MESSAGE_RE = /\b(metamask|ethereum|web3|phantom wallet|coinbase wallet|wallet_request|inpage\.js|injected provider|Receiving end does not exist)\b/i

/**
 * True when an ErrorEvent / PromiseRejectionEvent originates from a browser
 * extension (MetaMask, Phantom, Coinbase Wallet, ad-blockers, etc.) rather
 * than from Onboardix code. We never want these in the user-facing toast or
 * in the support-ticket buffer — they are noise the user can't act on.
 */
export function isThirdPartyError(event) {
    if (!event) return false
    const sources = [
        event.filename,
        event.error?.stack,
        event.reason?.stack,
        event.reason?.fileName,
    ]
    for (const s of sources) {
        if (typeof s === 'string' && _EXTENSION_URL_RE.test(s)) return true
    }
    const msg = event.message || event.error?.message || event.reason?.message || event.reason
    if (typeof msg === 'string' && _WALLET_MESSAGE_RE.test(msg)) return true
    return false
}

/**
 * True when the error is a benign offline / offline-replay signal raised by
 * apiFetch (core.js sets `err.isOffline` / `err.offlineReplayed`). In that state
 * the user's mutation is safely queued by SyncManager and will replay when the
 * connection returns — it is a self-healing condition surfaced calmly by
 * SyncStatusBar / NetworkToast, NOT an uncaught application bug.
 *
 * Such errors must never reach the error buffer or the AutoErrorToast: doing so
 * presents "Изменения сохранены и отправятся автоматически…" as a scary
 * "Произошла ошибка" and nudges the user to file a (high-severity) support
 * ticket for a transient network blip (e.g. a Railway cold-start / 502 during a
 * deploy). Control flow keys off the structured flags, per core.js contract.
 */
export function isBenignOfflineError(event) {
    if (!event) return false
    const err = event.reason || event.error || event
    return !!(err && (err.isOffline || err.offlineReplayed || isExpectedAbort(err)))
}

function _formatError(payload) {
    return {
        at: new Date().toISOString(),
        message: typeof payload?.message === 'string' ? payload.message.slice(0, 500) : String(payload).slice(0, 500),
        stack: payload?.stack ? String(payload.stack).slice(0, 1500) : null,
        source: payload?.filename || payload?.source || null,
    }
}

export function setupSupportContext() {
    if (_installed || typeof window === 'undefined') return
    _installed = true

    window.addEventListener('error', (event) => {
        if (isThirdPartyError(event) || isBenignOfflineError(event)) return
        _pushBounded(_errors, _formatError({
            message: event.message || event.error?.message || 'window.onerror',
            stack: event.error?.stack,
            filename: event.filename,
        }), ERROR_BUFFER_LIMIT)
    })

    window.addEventListener('unhandledrejection', (event) => {
        if (isThirdPartyError(event) || isBenignOfflineError(event)) return
        const reason = event.reason
        _pushBounded(_errors, _formatError({
            message: reason?.message || String(reason).slice(0, 500) || 'unhandledrejection',
            stack: reason?.stack,
        }), ERROR_BUFFER_LIMIT)
    })

    // Network failures: passively read performance.getEntriesByType('resource')
    // when the buffer is requested. No fetch override — that would conflict
    // with apiFetch's policy layer (timeout/retry/auth).
}

export function getRecentErrors() {
    return _errors.slice(-ERROR_BUFFER_LIMIT)
}

export function getRecentNetworkFailures() {
    if (typeof performance === 'undefined' || !performance.getEntriesByType) return []
    const now = performance.now()
    const since = now - NETWORK_WINDOW_MS
    const resources = performance.getEntriesByType('resource') || []
    const failures = []
    for (const r of resources.slice(-200)) {
        if (r.startTime < since) continue
        // Heuristic: zero transferSize on a non-cached request often indicates failure.
        if (r.responseStatus && r.responseStatus >= 400) {
            failures.push({
                url: String(r.name).slice(0, 256),
                status: r.responseStatus,
                duration_ms: Math.round(r.duration || 0),
            })
        }
    }
    if (failures.length > NETWORK_BUFFER_LIMIT) failures.splice(0, failures.length - NETWORK_BUFFER_LIMIT)
    return failures
}

export function getBrowserMetaSnapshot() {
    const meta = {
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
        language: typeof navigator !== 'undefined' ? navigator.language : null,
        platform: typeof navigator !== 'undefined' ? navigator.platform : null,
        timestamp: new Date().toISOString(),
        console_errors_last_20: getRecentErrors(),
        network_failures_last_60s: getRecentNetworkFailures(),
    }
    return meta
}
